import { LayoutGrid } from 'lucide-react';
import { createElement, lazy, Suspense, type ComponentType } from 'react';
import type { FeatureModule } from '@/app/contract';
import { APP_ROUTES, spacesPath, newItemPath, newProjectPath } from '@/app/paths';
import {registerFeatureRuntime} from '@/app/runtimes';
import { registerNamespace } from '@/i18n';
import { registerMessageAction, registerMessageAccessory } from '@/features/chat/registry';
import { registerProposalReviewer } from '@/features/agents/public';
import { CreateItemFromMessage } from './public';
import { rememberMessageSource } from './data/message-source';
import en from './locales/en.json';
import zh from './locales/zh-Hans.json';
import {SpacesRuntime} from './Runtime';
registerNamespace('spaces', { en, 'zh-Hans': zh });
registerFeatureRuntime('spaces.cleanup',SpacesRuntime,{phase:'services'});
const Root = lazy(() => import('./Root'));
const Alias = lazy(async () => ({ default: (await import('./Root')).ProjectsAlias }));
const Item = lazy(() => import('./items/Detail'));
const NumberItem = lazy(async () => ({ default: (await import('./items/Routes')).NumberRoute }));
const NewItem = lazy(async () => ({ default: (await import('./items/Routes')).NewItemRoute }));
const NewProjectRoute = lazy(async () => ({ default: (await import('./items/Routes')).NewProjectRoute }));
const Project = lazy(() => import('./projects/Detail'));
const Members = lazy(() => import('./projects/Members'));
const Settings = lazy(() => import('./projects/Settings'));
const Context = lazy(() => import('./projects/Context'));
const Glossary = lazy(async () => ({ default: (await import('./projects/Context')).GlossaryRoute }));
const Memory = lazy(() => import('./projects/Memory'));
const Files = lazy(() => import('./files/Browser'));
const File = lazy(() => import('./files/Preview'));
const RedirectFiles = lazy(() => import('./files/Redirect'));
const RunArtifact = lazy(() => import('./files/RunArtifact'));
const Trash = lazy(async () => ({ default: (await import('./files/History')).TrashRoute }));
const Surface = lazy(() => import('./projects/Surfaces'));
const Journal = lazy(() => import('./projects/Journal'));
const MyFiles = lazy(() => import('./files/MyFiles'));
const References = lazy(() => import('./items/MessageReferences'));
const Reviewer = lazy(() => import('./items/ProposalReview'));
// The shell also lazily resolves a registered route. Its default must be a function,
// rather than another LazyExoticComponent (React does not recursively unwrap defaults).
function routeScreen(Component: ComponentType): ComponentType { return function SpacesRoute() { return createElement(Suspense, { fallback: null }, createElement(Component)); }; }
registerMessageAction({ id: 'spaces.create-item', titleKey: 'spaces:editor.createItemFromMessage', icon: LayoutGrid,
    isAvailable: ({ message }) => !message.deleted_at && message.seq > 0 && message.kind === 'user' && message.parts.some(part => part.type === 'text' && typeof part.text === 'string'),
    run: context => {
        if (context.message.deleted_at)
            return;
        rememberMessageSource(context.services, context.message);
        const handle = context.navigation.present(createElement(CreateItemFromMessage, { messageId: context.message.id, onClose: () => handle.dismiss() }), { size: 'large' });
    },
});
registerMessageAccessory(props => createElement(Suspense, { fallback: null }, createElement(References, props)));
for (const kind of ['item.mutate', 'item.batch', 'access.change', 'label.manage'])
    registerProposalReviewer(kind, props => createElement(Suspense, { fallback: null }, createElement(Reviewer, props)));
const feature: FeatureModule = {
    id: 'spaces',
    section: { id: 'spaces', titleKey: 'spaces:title', icon: LayoutGrid },
    commands: [
        { id: 'spaces.items', titleKey: 'spaces:common.items', icon: LayoutGrid, run: nav => nav.open(spacesPath('items')) },
        { id: 'spaces.projects', titleKey: 'spaces:common.projects', icon: LayoutGrid, run: nav => nav.open(spacesPath('projects')) },
        { id: 'spaces.newItem', titleKey: 'spaces:common.newItem', run: nav => nav.open(newItemPath(nav.section)) },
        { id: 'spaces.newProject', titleKey: 'spaces:common.newProject', run: nav => nav.open(newProjectPath(nav.section)) },
    ],
    routes: [
        { ...APP_ROUTES.spaces, component: Root }, { ...APP_ROUTES.projectsAlias, component: Alias },
        { ...APP_ROUTES.item, component: Item }, { ...APP_ROUTES.itemNumber, component: NumberItem },
        { ...APP_ROUTES.newItem, component: NewItem }, { ...APP_ROUTES.newProject, component: NewProjectRoute },
        { ...APP_ROUTES.project, component: Project }, { ...APP_ROUTES.projectMembers, component: Members },
        { ...APP_ROUTES.projectSettings, component: Settings }, { ...APP_ROUTES.projectContext, component: Context },
        { ...APP_ROUTES.projectGlossary, component: Glossary }, { ...APP_ROUTES.projectMemory, component: Memory },
        { ...APP_ROUTES.projectFiles, component: Files }, { ...APP_ROUTES.projectFile, component: File },
        { ...APP_ROUTES.projectFolder, component: RedirectFiles }, { ...APP_ROUTES.projectFolderFile, component: RunArtifact },
        { ...APP_ROUTES.projectHistory, component: RedirectFiles }, { ...APP_ROUTES.projectTrash, component: Trash },
        { ...APP_ROUTES.projectSurface, component: Surface }, { ...APP_ROUTES.chatJournal, component: Journal },
        { ...APP_ROUTES.myFiles, component: MyFiles },
    ].map(route => ({ ...route, component: routeScreen(route.component) })),
};
export default feature;
export { ItemDetail, CreateTask, NewProject, CreateItemFromMessage, ChatProjectSheet, useCalendarBoardFacts, useItemLocalPresentation, type ItemDetailProps, type CreateTaskProps, type NewProjectProps, type CreateItemFromMessageProps, type ChatProjectSheetProps, type CalendarBoardFacts, type ItemLocalPresentation, } from './public';
